/**
 * Alpha01 Design System Tokens
 * 
 * Centralized design tokens for consistent theming across the application.
 * Use these tokens instead of hard-coded values for maintainability and consistency.
 */

export const tokens = {
  color: {
    // Primary brand colors from existing theme
    primary: '#1DB4A3',      // Teal
    primaryLight: '#5EEAE5',
    primaryDark: '#1B9E99',
    
    // Secondary/accent colors
    accent: '#E53935',       // Coral/Red
    accentLight: '#FF8080',
    accentDark: '#E54A4A',
    
    // Navy colors
    navy: '#0A4A6E',
    navyDark: '#121212',
    navyLight: '#1A6A9E',
    
    // Purple for active states
    purple: '#6366F1',
    purpleLight: '#8183F4',
    purpleDark: '#4F51C0',
    
    // Semantic colors
    success: '#34C759',
    successLight: '#5FD47F',
    successDark: '#2A9F47',
    
    danger: '#FF3B30',
    dangerLight: '#FF6B61',
    dangerDark: '#CC2F26',
    
    warning: '#FF9500',
    warningLight: '#FFB340',
    warningDark: '#CC7700',
    
    info: '#0A4A6E',
    infoLight: '#1A6A9E',
    infoDark: '#121212',
    
    // Neutral palette
    neutral: {
      50: '#F7FBFB',
      100: '#EBF4F4',
      200: '#DCE9E9',
      300: '#CBD8D8',
      400: '#9FB3B3',
      500: '#738E8E',
      600: '#576B6B',
      700: '#334B4B',
      800: '#1F3535',
      900: '#0E1D1D',
    },
    
    // Background colors
    background: {
      default: '#F5F7FA',
      paper: '#FFFFFF',
      subtle: '#F7FBFB',
    },
    
    // Text colors
    text: {
      primary: '#333333',
      secondary: '#666666',
      disabled: '#9E9E9E',
      hint: '#BDBDBD',
    },
  },
  
  // Border radius scale
  radius: {
    xs: 4,
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
    xxl: 20,
    round: 9999,
  },
  
  // Spacing scale (8px base)
  space: {
    x0: 0,
    x1: 4,
    x2: 8,
    x3: 12,
    x4: 16,
    x5: 20,
    x6: 24,
    x7: 28,
    x8: 32,
    x10: 40,
    x12: 48,
    x16: 64,
    x20: 80,
    x24: 96,
  },
  
  // Shadow scale
  shadow: {
    none: 'none',
    xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
    sm: '0 2px 4px rgba(0, 0, 0, 0.05)',
    md: '0 4px 8px rgba(0, 0, 0, 0.06)',
    lg: '0 6px 12px rgba(0, 0, 0, 0.08)',
    xl: '0 8px 16px rgba(0, 0, 0, 0.1)',
    card: '0 4px 12px rgba(0, 0, 0, 0.05)',
    hover: '0 6px 16px rgba(0, 0, 0, 0.08)',
  },
  
  // Typography scale
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem',// 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
  },
  
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.6,
    loose: 2,
  },
  
  // Z-index scale
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
  
  // Transition durations
  transition: {
    fast: '150ms',
    base: '200ms',
    slow: '300ms',
    slower: '500ms',
  },
  
  // Breakpoints (matches MUI defaults)
  breakpoint: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
  
  // Common sizes
  size: {
    buttonHeight: 40,
    inputHeight: 40,
    iconButton: 44,  // Minimum touch target per a11y guidelines
    avatar: {
      sm: 32,
      md: 40,
      lg: 56,
      xl: 80,
    },
  },
};

/**
 * Helper function to get token value with fallback
 * @param {string} path - Dot notation path to token (e.g., 'color.primary')
 * @param {*} fallback - Fallback value if token not found
 * @returns {*} Token value or fallback
 */
export const getToken = (path, fallback = undefined) => {
  const keys = path.split('.');
  let value = tokens;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return fallback;
    }
  }
  
  return value;
};

export default tokens;
